import apiCall from '@/utils/apiCall';
import { calculateFileSHA1 } from '@/utils/file';
import axios from 'axios';
import { find, findIndex } from 'lodash';

export default {
    /**
     * Loads show episodes.
     *
     * @param  {object}    state          Vuex state.
     * @param  {Function}  state.commit   Method to commit a mutation.
     * @param  {object}    payload        Action payload.
     * @param  {string}    payload.hash   Hash of the show.
     * @param  {object}    payload.query  Query parameters.
     */
    async loadEpisodes({ commit }, { hash, query }) {
        const { status, data } = await apiCall(this._vm, 'get', `/api/shows/${hash}/episodes`, {
            params: {
                ...query,
                limit: 10,
            },
        });

        if (200 === status) {
            const { data: episodes, meta } = data;

            commit('setEpisodes', episodes);
            commit('setTotalPages', meta.last_page);
        } else {
            commit('setEpisodes', false);
            commit('setTotalPages', 0);
        }
    },

    /**
     * Loads episodes count.
     *
     * @param  {object}    state          Vuex state.
     * @param  {Function}  state.commit   Method to commit a mutation.
     * @param  {object}    payload        Action payload.
     * @param  {string}    payload.hash   Hash of the show.
     * @param  {object}    payload.query  Query parameters.
     */
    async loadEpisodesCount({ commit }, { hash, query }) {
        const { status, data } = await apiCall(this._vm, 'get', `/api/shows/${hash}/episodes/count`, {
            params: { ...query },
        });

        if (200 === status) {
            commit('setEpisodesCount', data);
        } else {
            commit('setEpisodesCount', {});
        }
    },

    /**
     * Update Episode in store.
     *
     * @param  {object}    context             Vuex context.
     * @param  {Function}  context.commit      Method to commit a mutation.
     * @param  {object}    context.state       Vuex state.
     * @param  {object}    payload             Action payload.
     * @param  {number}    payload.episodeId   Episode ID.
     * @param  {object}    payload.data        Episode data to update.
     */
    updateStoreEpisode({ commit, state }, { episodeId, data }) {
        const episodes = [...state.episodes];
        const index = findIndex(episodes, { id: parseInt(episodeId) });

        if (0 > index) {
            return;
        }

        episodes.splice(index, 1, {
            ...find(episodes, { id: parseInt(episodeId) }),
            ...data,
        });

        commit('setEpisodes', episodes);
    },

    /**
     * Get upload data for Episode audio.
     *
     * @param  {object} state         Vuex state.
     * @param  {object} payload       Action payload.
     * @param  {string} payload.hash  Hash of the show.
     * @param  {File}   payload.file  File to upload.
     */
    async getUploadData(state, { hash, file }) {
        const { status, data } = await apiCall(this._vm, 'post', `/api/shows/${hash}/episodes/upload`, {
            extension: file.name.split('.').pop(),
        });

        if (201 === status) {
            return data;
        }

        throw new Error('Cannot fetch upload data.');
    },

    /**
     * Uploads episode file.
     *
     * @param  {object} state               Vuex state.
     * @param  {object} payload             Action payload.
     * @param  {File}   payload.file        File to upload.
     * @param  {object} payload.uploadData  Data required to upload audio.
     * @param  {object} payload.options     axios options.
     */
    async uploadEpisode(state, { file, uploadData, options }) {
        return new Promise((resolve) => {
            /**
             * Handles file upload to remote server.
             */
            const callback = async () => {
                await axios.post(uploadData.uploadUrl, file, {
                    ...options,
                    withCredentials: false, // Disable axios credentials to not send x-xsrf-token
                    headers: {
                        'Content-Type': 'application/octet-stream',
                        Authorization: uploadData.authorizationToken,
                        'X-Bz-File-Name': uploadData.filename,
                        'X-Bz-Content-Sha1': await calculateFileSHA1(file),
                    },
                });

                resolve({
                    url: uploadData.fileUrl,
                });

                reader.removeEventListener('load', callback);
            };

            const reader = new FileReader();
            reader.addEventListener('load', callback);
            reader.readAsBinaryString(file);
        });
    },

    /**
     * Store Episode.
     *
     * @param  {object} state              Vuex state.
     * @param  {object} payload            Action payload.
     * @param  {string} payload.hash       Hash of the show.
     * @param  {object} payload.data       Episode data.
     */
    async storeEpisode(state, { hash, data }) {
        return await apiCall(this._vm, 'post', `/api/shows/${hash}/episodes`, data);
    },

    /**
     * Update Episode.
     *
     * @param  {object} state              Vuex state.
     * @param  {object} payload            Action payload.
     * @param  {string} payload.hash       Hash of the show.
     * @param  {string} payload.episodeId  ID of the episode.
     * @param  {object} payload.data       Episode data.
     */
    async updateEpisode(state, { hash, episodeId, data }) {
        return await apiCall(this._vm, 'put', `/api/shows/${hash}/episodes/${episodeId}`, data);
    },

    /**
     * Delete Episode.
     *
     * @param  {object} state              Vuex state.
     * @param  {object} payload            Action payload.
     * @param  {string} payload.hash       Hash of the show.
     * @param  {string} payload.episodeId  ID of the episode.
     */
    async deleteEpisode(state, { hash, episodeId }) {
        return await apiCall(this._vm, 'delete', `/api/shows/${hash}/episodes/${episodeId}`);
    },
};
